import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { EtapaProceso, ProcesoTrabajo } from "../../../shared/types";
import { useAuth } from "../../auth/AuthContext";
import { Cargando } from "../../components/Cargando";
import { EncabezadoPagina } from "../../components/EncabezadoPagina";
import { EstadoVacio } from "../../components/EstadoVacio";
import { Select } from "../../components/Formulario";
import { Insignia } from "../../components/Insignia";
import { SelectorVistaProceso } from "../../components/SelectorVistaProceso";
import { useToast } from "../../components/Toast";
import { useEstadoEnUrl } from "../../hooks/useEstadoEnUrl";
import { api, ErrorApi } from "../../lib/apiClient";

interface TarjetaExpediente {
  id: string;
  numero_expediente: string;
  etapa_actual_id: string | null;
  etapa_desde: string | null;
  etapa_responsable_id: string | null;
  etapa_responsable_nombre: string | null;
  cliente_nombre: string | null;
  dias_en_etapa: number | null;
  etapa_vencida: boolean;
}

interface Columna {
  etapa: EtapaProceso;
  expedientes: TarjetaExpediente[];
}

// Vista Kanban del Motor de Procesos de Trabajo: columnas = etapas del
// proceso elegido, tarjetas = expedientes asignados a ese proceso. Drag &
// drop nativo HTML5 (sin librería nueva) — al soltar, PATCH
// /expedientes/:id/etapa dispara historial/checklist/automatizaciones del
// lado del servidor exactamente igual que moverlo desde la pestaña Proceso
// de la ficha. Ver routes/procesos.ts (GET /:id/kanban) y
// routes/expedientes.ts (PATCH /:id/etapa).
//
// El proceso elegido vive en ?proceso= (useEstadoEnUrl, no un useState
// suelto): así el <Select> queda sincronizado en ambos sentidos con la URL
// (antes sólo se leía al montar) y el botón Volver / el switcher de vistas
// pueden armar links que preservan exactamente el proceso abierto.
export function KanbanProcesoPage() {
  const { permisos } = useAuth();
  const puedeMover = permisos.has("procesos.mover_etapa");
  const { mostrar } = useToast();
  const [procesoIdUrl, setProcesoIdUrl] = useEstadoEnUrl("proceso", "");

  const [procesos, setProcesos] = useState<ProcesoTrabajo[]>([]);
  const [columnas, setColumnas] = useState<Columna[] | null>(null);
  const [cargando, setCargando] = useState(true);
  const [arrastrandoId, setArrastrandoId] = useState<string | null>(null);
  const [columnaSobreId, setColumnaSobreId] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ items: ProcesoTrabajo[] }>("/procesos")
      .then((d) => {
        const activos = d.items.filter((p) => p.activo);
        setProcesos(activos);
        if (activos.length === 0) {
          setCargando(false);
          return;
        }
        const preseleccionado = activos.some((p) => p.id === procesoIdUrl) ? procesoIdUrl : activos[0].id;
        if (preseleccionado !== procesoIdUrl) setProcesoIdUrl(preseleccionado);
      })
      .catch(() => {
        mostrar("No se pudieron cargar los procesos.", "error");
        setCargando(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function cargarKanban(id: string) {
    setCargando(true);
    try {
      const datos = await api.get<{ proceso: ProcesoTrabajo; columnas: Columna[] }>(`/procesos/${id}/kanban`);
      setColumnas(datos.columnas);
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo cargar el Kanban.", "error");
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => {
    if (procesoIdUrl) cargarKanban(procesoIdUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [procesoIdUrl]);

  async function moverExpediente(expedienteId: string, etapaId: string) {
    try {
      await api.patch(`/expedientes/${expedienteId}/etapa`, { etapa_id: etapaId });
      await cargarKanban(procesoIdUrl);
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo mover el expediente de etapa.", "error");
    }
  }

  if (procesos.length === 0 && !cargando) {
    return (
      <div>
        <EncabezadoPagina
          titulo="Procesos de Trabajo"
          descripcion="Vista Kanban por etapas."
          volver={{
            rutaPadre: procesoIdUrl ? `/configuracion/procesos?proceso=${procesoIdUrl}&vista=visual` : "/configuracion/procesos",
            etiqueta: "Volver al Diseñador Visual",
          }}
        />
        <EstadoVacio
          titulo="Todavía no hay ningún Proceso configurado"
          descripcion="Configurá un Proceso de Trabajo (y sus Etapas) desde Configuración → Procesos de Trabajo para empezar a usar el Kanban."
          accion={<Link to="/configuracion/procesos" className="text-sm font-medium text-secundario hover:underline">Ir a Procesos de Trabajo</Link>}
        />
      </div>
    );
  }

  const procesoActual = procesos.find((p) => p.id === procesoIdUrl);

  return (
    <div>
      <EncabezadoPagina
        titulo="Procesos de Trabajo"
        descripcion="Vista Kanban por etapas del proceso elegido."
        volver={{
          rutaPadre: procesoIdUrl ? `/configuracion/procesos?proceso=${procesoIdUrl}&vista=visual` : "/configuracion/procesos",
          etiqueta: "Volver al Diseñador Visual",
        }}
        migas={[
          { etiqueta: "Configuración", ruta: "/configuracion" },
          { etiqueta: "Procesos de Trabajo", ruta: "/configuracion/procesos" },
          ...(procesoActual ? [{ etiqueta: procesoActual.nombre }] : []),
          { etiqueta: "Vista Kanban" },
        ]}
        pestanas={procesoIdUrl && <SelectorVistaProceso procesoId={procesoIdUrl} vistaActual="kanban" />}
      />

      <div className="mb-5 max-w-xs">
        <Select value={procesoIdUrl} onChange={(e) => setProcesoIdUrl(e.target.value)}>
          {procesos.map((p) => (
            <option key={p.id} value={p.id}>
              {p.nombre}
            </option>
          ))}
        </Select>
      </div>

      {cargando || !columnas ? (
        <Cargando />
      ) : columnas.length === 0 ? (
        <EstadoVacio
          titulo="Este proceso todavía no tiene etapas"
          descripcion="Agregá etapas desde Configuración → Procesos de Trabajo."
        />
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {columnas.map((col) => (
            <div
              key={col.etapa.id}
              onDragOver={(e) => {
                if (!puedeMover) return;
                e.preventDefault();
                setColumnaSobreId(col.etapa.id);
              }}
              onDragLeave={() => setColumnaSobreId((cur) => (cur === col.etapa.id ? null : cur))}
              onDrop={(e) => {
                e.preventDefault();
                setColumnaSobreId(null);
                const expedienteId = e.dataTransfer.getData("text/plain");
                if (puedeMover && expedienteId && expedienteId !== col.etapa.id) {
                  moverExpediente(expedienteId, col.etapa.id);
                }
              }}
              className={`w-72 shrink-0 rounded-xl border bg-superficie p-3 shadow-sm transition-colors ${
                columnaSobreId === col.etapa.id ? "border-secundario bg-secundario/5" : "border-borde"
              }`}
            >
              <div className="mb-3 flex items-center gap-2 px-1">
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full border border-borde"
                  style={{ backgroundColor: col.etapa.color ?? undefined }}
                />
                <h3 className="truncate text-sm font-semibold text-texto">{col.etapa.nombre}</h3>
                <span className="ml-auto shrink-0 rounded-full bg-fondo px-2 py-0.5 text-xs text-texto-secundario">
                  {col.expedientes.length}
                </span>
              </div>
              <div className="space-y-2">
                {col.expedientes.map((exp) => (
                  <div
                    key={exp.id}
                    draggable={puedeMover}
                    onDragStart={(e) => {
                      e.dataTransfer.setData("text/plain", exp.id);
                      setArrastrandoId(exp.id);
                    }}
                    onDragEnd={() => setArrastrandoId(null)}
                    className={`rounded-lg border border-borde bg-fondo p-2.5 text-sm shadow-sm ${
                      puedeMover ? "cursor-grab active:cursor-grabbing" : ""
                    } ${arrastrandoId === exp.id ? "opacity-40" : ""}`}
                  >
                    <Link
                      to={`/expedientes/${exp.id}`}
                      state={{ from: `/procesos?proceso=${procesoIdUrl}` }}
                      className="block truncate font-medium text-texto hover:text-secundario hover:underline"
                    >
                      {exp.numero_expediente}
                    </Link>
                    {exp.cliente_nombre && (
                      <p className="truncate text-xs text-texto-secundario">{exp.cliente_nombre}</p>
                    )}
                    <div className="mt-1.5 flex items-center justify-between gap-2">
                      <span className="truncate text-xs text-texto-secundario">
                        {exp.etapa_responsable_nombre ?? "Sin responsable"}
                      </span>
                      {exp.dias_en_etapa != null && (
                        <Insignia
                          texto={`${exp.dias_en_etapa}d`}
                          claveColor={exp.etapa_vencida ? "Vencido" : "Pendiente"}
                        />
                      )}
                    </div>
                  </div>
                ))}
                {col.expedientes.length === 0 && (
                  <p className="px-1 py-4 text-center text-xs text-texto-secundario">Sin expedientes</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
